import * as DashboardActions from './dashboard.actions';
import { IAppState } from '../../store/app.state';
import { Action, createReducer, createSelector, on } from '@ngrx/store';
import { DashboardModel, IDashboardProject, IDashboardProjectsFilter } from '../models/dashboard.model';
import moment from 'moment';

export type State = DashboardModel;

const initialState: State = {
  projects: null,
  filters: null,
  checks: null,
  loaders: {
    projects: true,
    checks: false
  }
};

const dashboardReducer = createReducer(
  initialState,
  on(
    DashboardActions.GetProjects,
    (state): State => ({
      ...state,
      loaders: {
        ...state.loaders,
        projects: true
      }
    })
  ),

  on(
    DashboardActions.GetProjectsFailed,
    (state): State => ({
      ...state,
      loaders: {
        ...state.loaders,
        projects: false
      }
    })
  ),

  on(DashboardActions.GetProjectsSuccess, (state, { projects }): State => {
    return {
      ...state,
      projects,
      loaders: {
        ...state.loaders,
        projects: false
      }
    };
  }),

  on(
    DashboardActions.GetChecks,
    (state): State => ({
      ...state,
      loaders: {
        ...state.loaders,
        checks: true
      }
    })
  ),

  on(
    DashboardActions.GetChecksFailed,
    (state): State => ({
      ...state,
      loaders: {
        ...state.loaders,
        checks: false
      }
    })
  ),

  on(DashboardActions.GetChecksSuccess, (state, { checks }): State => {
    return {
      ...state,
      checks,
      loaders: {
        ...state.loaders,
        checks: false
      }
    };
  }),

  on(DashboardActions.ApplyFilters, (state, { filters }): State => {
    return {
      ...state,
      filters: { ...state.filters, ...filters }
    };
  }),

  on(DashboardActions.ClearProjects, (state): State => {
    return {
      ...state,
      projects: null,
      filters: null
    };
  })
);

export const reducer = (state: State, action: Action): State => dashboardReducer(state, action);
export const dashboardState = (state: IAppState) => state.dashboard;

export const getLoader = createSelector(
  dashboardState,
  (state: DashboardModel) => state.loaders.checks || state.loaders.projects
);
export const getProjectsLoader = createSelector(dashboardState, (state: DashboardModel) => state.loaders.projects);
export const getChecksLoader = createSelector(dashboardState, (state: DashboardModel) => state.loaders.checks);

export const getChecks = createSelector(dashboardState, (state: DashboardModel) => state.checks);
export const getFilters = createSelector(dashboardState, (state: DashboardModel) => state.filters);

export const getProjects = createSelector(dashboardState, (state: DashboardModel) => {
  const { filters, projects } = state;
  if (projects?.length === 0) return null;
  if (!filters) return projects;
  return projects?.filter((project) => dashboardFilter(project, filters));
});

const dashboardFilter = (project: IDashboardProject, filters: IDashboardProjectsFilter) => {
  const matchType = filters?.type?.length
    ? filters.type.map((t) => t.toLowerCase()).includes(project.type.toLowerCase())
    : true;
  const matchTherapeuticArea = filters?.therapeuticArea?.length
    ? filters.therapeuticArea.map((t) => t.toLowerCase()).includes(project.therapeuticArea?.toLowerCase())
    : true;
  const matchName = filters.name ? project.name.toLowerCase().includes(filters.name.toLowerCase()) : true;
  const matchProtocol = filters.protocol
    ? project.protocol && project.protocol.toLowerCase().includes(filters.protocol.toLowerCase())
    : true;
  const machCreatedFrom = filters.createdAtFrom
    ? moment(filters.createdAtFrom).isBefore(moment(new Date(project.createdAt)))
    : true;
  const machCreatedTo = filters.createdAtTo
    ? moment(filters.createdAtTo)
        .add(1, 'day')
        .isAfter(moment(new Date(project.createdAt)))
    : true;
  return machCreatedFrom && machCreatedTo && matchType && matchTherapeuticArea && matchName && matchProtocol;
};
